import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import {
  Calculator, ArrowLeft, History, Trash2, Delete,
  Sigma, Divide, X as MultiplyIcon, Plus, Minus, Equal,
  RotateCcw, Superscript, Sparkles
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface HistoryItem {
  id: string;
  expression: string;
  result: string;
  timestamp: Date;
}

const IndependentCalculatorView = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(''); // Current calculated result
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [angleMode, setAngleMode] = useState<'DEG' | 'RAD'>('DEG');
  const [isShift, setIsShift] = useState(false);
  const [memory, setMemory] = useState<number>(0);
  const [ans, setAns] = useState<string>('0');

  const historyEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-scroll history
  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const handleInput = (val: string) => {
    setInput(prev => prev + val);
    setIsShift(false); // Reset shift after action
  };

  const clear = () => {
    setInput('');
    setResult('');
  };

  const backspace = () => {
    setInput(prev => prev.slice(0, -1));
  };

  // --- Math Logic Engine ---
  const factorial = (n: number): number => {
      if (n < 0) return NaN;
      if (n === 0 || n === 1) return 1;
      let res = 1;
      for (let i = 2; i <= n; i++) res *= i;
      return res;
  };

  const nPr = (n: number, r: number): number => {
      return factorial(n) / factorial(n - r);
  };

  const nCr = (n: number, r: number): number => {
      return factorial(n) / (factorial(r) * factorial(n - r));
  };

  const calculate = () => {
    try {
      if (!input) return;

      // 1. Pre-processing: Handle Implied Multiplications and Syntax
      let evalString = input
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/π/g, 'Math.PI')
        .replace(/e/g, 'Math.E')
        .replace(/\^/g, '**')
        .replace(/√\(/g, 'Math.sqrt(')
        .replace(/Ans/g, ans);

      // Handle Percent % (e.g. 50 + 10% -> 50 + 50*0.10? Or just /100? Standard sci calc: 10% = 0.1)
      // We will treat % as /100 for now if simpler, or strictly as n/100.
      evalString = evalString.replace(/(\d+(\.\d+)?)%/g, '($1/100)');

      // Handle Factorial !
      // Regex find number before ! -> factorial(number)
      // Matches 5! or (2+3)! --> Needs bracket matching, simple regex works for integers.
      // We'll replace `!` with `scope.fact(...)` wrapping is hard with regex reversed.
      // Quicker hack: define a custom factorial function on Number prototype? No.
      // Solution: Recursive parser is best, but for MVP we wrap the logic in a custom `eval` scope.
      // Let's rely on user typing `fact(` via button if shift? Or just `!`?
      // For `!`, let's try a regex for simple numbers: `(\d+)!` -> `scope.fact($1)`
      evalString = evalString.replace(/(\d+)!/g, 'scope.fact($1)');
      // For parens: `(\(.*\))!` -> `scope.fact($1)` (greedy match risk).
      // We'll trust user syntax for now or offer `fact()` button.
      // actually, let's just expose `fact(` on the button for safety.

      // Handle nPr and nCr infix: "5 P 3"
      // Regex: number P number
      evalString = evalString.replace(/(\d+(\.\d+)?)\s*P\s*(\d+(\.\d+)?)/g, 'scope.nPr($1, $3)');
      evalString = evalString.replace(/(\d+(\.\d+)?)\s*C\s*(\d+(\.\d+)?)/g, 'scope.nCr($1, $3)');

      // Trig Scope
      const toRad = angleMode === 'DEG' ? (Math.PI / 180) : 1;
      const fromRad = angleMode === 'DEG' ? (180 / Math.PI) : 1;

      const scope = {
        sin: (x: number) => Math.sin(x * toRad),
        cos: (x: number) => Math.cos(x * toRad),
        tan: (x: number) => Math.tan(x * toRad),
        asin: (x: number) => Math.asin(x) * fromRad,
        acos: (x: number) => Math.acos(x) * fromRad,
        atan: (x: number) => Math.atan(x) * fromRad,
        sinh: Math.sinh,
        cosh: Math.cosh,
        tanh: Math.tanh,
        asinh: Math.asinh,
        acosh: Math.acosh,
        atanh: Math.atanh,
        log: Math.log10,
        ln: Math.log,
        sqrt: Math.sqrt,
        cbrt: Math.cbrt,
        abs: Math.abs,
        pow: Math.pow,
        PI: Math.PI,
        E: Math.E,
        fact: factorial,
        nPr: nPr,
        nCr: nCr
      };

      // Wrap function calls with `scope.`
      // We must be careful not to double replace.
      const functions = Object.keys(scope);
      functions.forEach(fn => {
         // Replace function names that are followed by `(` 
         // We use a regex that looks for word boundary to avoid `asinh` matching `sin`
         // Sort by length desc handled by loop order? No, `asinh` constains `sin`.
         // We'll iterate manually list hardcoded safest.
      });

      // Simple brute force replacement for safety (longest first)
      const replacements = ['asinh', 'acosh', 'atanh', 'sinh', 'cosh', 'tanh', 'asin', 'acos', 'atan', 'sin', 'cos', 'tan', 'log', 'ln', 'sqrt', 'cbrt', 'fact', 'nPr', 'nCr'];
      replacements.forEach(fn => {
          const regex = new RegExp(`\\b${fn}\\(`, 'g');
          evalString = evalString.replace(regex, `scope.${fn}(`);
      });

      // eslint-disable-next-line
      const calcFunc = new Function('scope', `return ${evalString}`);
      const res = calcFunc(scope);
      
      const resStr = String(res);
      const formattedRes = Number(res).toLocaleString(undefined, { maximumFractionDigits: 10 });
      
      setResult(formattedRes);
      setAns(resStr); // Store raw for next calc
      
      setHistory(prev => [
        ...prev, 
        { 
          id: Math.random().toString(36), 
          expression: input, 
          result: formattedRes, 
          timestamp: new Date() 
        }
      ]);
    } catch (err) {
      setResult('Syntax Error');
    }
  };

  const memoryStore = () => {
     const val = parseFloat(result || ans);
     if (!isNaN(val)) setMemory(val);
  };

  const memoryRecall = () => {
     setInput(prev => prev + memory);
  };

  const memoryClear = () => {
     setMemory(0);
  };

  // UI Components
  const Btn = ({ 
    label, val, shiftLabel, shiftVal, type = 'default', wide = false 
  }: { 
    label: React.ReactNode, 
    val?: string, 
    shiftLabel?: string, 
    shiftVal?: string,
    type?: 'default' | 'operator' | 'action' | 'scientific' | 'shift', 
    wide?: boolean 
  }) => {
    // Style logic
    let bgClass = "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-gray-50 dark:hover:bg-zinc-700 shadow-sm border border-gray-200 dark:border-zinc-700";
    if (type === 'operator') bgClass = "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 border-indigo-100 dark:border-indigo-500/20";
    if (type === 'action') bgClass = "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 border-red-100 dark:border-red-500/20";
    if (type === 'scientific') bgClass = "bg-zinc-100 dark:bg-black text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-900 border-zinc-200 dark:border-zinc-800";
    if (type === 'shift') bgClass = "bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-500 hover:bg-amber-200 dark:hover:bg-amber-900/30 border-amber-200 dark:border-amber-500/20";

    const activeLabel = (isShift && shiftLabel) ? shiftLabel : label;
    const activeVal = (isShift && shiftVal) ? shiftVal : val;

    return (
        <button 
           className={`relative h-7 md:h-8 rounded-lg font-medium transition-all active:scale-95 flex flex-col items-center justify-center ${bgClass} ${wide ? 'col-span-2' : ''}`}
           onClick={() => {
              if (type === 'shift') {
                  setIsShift(!isShift);
              } else if (activeVal) {
                  handleInput(activeVal);
              }
           }}
        >
            <span className={`text-sm md:text-base ${isShift && shiftLabel ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}`}>
                {activeLabel}
            </span>
            {/* Shift Hint */}
            {!isShift && shiftLabel && (
                <span className="absolute top-1 right-1.5 text-[8px] md:text-[10px] text-amber-600 dark:text-amber-500 font-bold opacity-80">
                    {shiftLabel}
                </span>
            )}
        </button>
    );
  };

  return (
    <div className="h-full flex flex-col gap-1 p-4 md:p-6 animate-in fade-in duration-500 relative">
      {/* Back Button — toggles to AI calculator, or falls back to utilities if we didn't come from it */}
      <button
        onClick={() => {
          const fromAI = (location.state as { from?: string } | null)?.from === 'ai-calculator';
          navigate(fromAI ? '/dashboard/utilities/ai-calculator' : '/dashboard/utilities');
        }}
        title="Go back"
        aria-label="Go back"
        className="absolute -top-1 -left-3 z-10 h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
      </button>

      {/* Header removed to fit in viewport */}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-0 items-start">
         {/* Calculator Main */}
         <div className="lg:col-span-2 flex flex-col gap-1 min-h-0">
            {/* LCD Display */}
            <div className="bg-white dark:bg-[#1a1c1a] border border-zinc-200 dark:border-4 dark:border-zinc-800 rounded-2xl p-2 shadow-sm dark:shadow-inner min-h-[50px] flex flex-col justify-between items-end relative overflow-hidden font-mono">
                {/* Status Bar */}
                <div className="absolute top-2 left-3 flex gap-4 text-[10px] font-bold text-zinc-400 select-none">
                    <span className={isShift ? "text-amber-600" : ""}>SHIFT</span>
                    <span className={angleMode === 'DEG' ? "text-zinc-900 dark:text-zinc-200" : ""}>DEG</span>
                    <span className={angleMode === 'RAD' ? "text-zinc-900 dark:text-zinc-200" : ""}>RAD</span>
                    <span className={memory !== 0 ? "text-zinc-900 dark:text-zinc-200" : ""}>M</span>
                </div>
                
                {/* Inputs */}
                <div className="w-full text-right text-zinc-600 dark:text-zinc-400 text-sm break-all mt-3 tracking-wider">
                    {input || '0'}
                </div>

                {/* Result */}
                <div className="w-full text-right text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tighter break-all">
                    {result || '0'}
                </div>
            </div>

            {/* Scientific Keypad - 4 rows, 6 columns */}
            <div className="grid grid-cols-6 gap-1 auto-rows-min">
                {/* Row 1: Controls */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><Btn label="SHIFT" type="shift" /></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Toggle shift mode to access secondary functions (shown in amber on each key).</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setAngleMode(m => m === 'DEG' ? 'RAD' : 'DEG')}
                      className="bg-zinc-100 dark:bg-black text-zinc-600 dark:text-zinc-300 text-xs font-bold rounded-lg border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800"
                    >
                      {angleMode}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Toggle angle mode: degrees (DEG) for geometry or radians (RAD) for calculus.</p></TooltipContent>
                </Tooltip>
                <Btn label="abs" val="abs(" type="scientific" />
                <Btn label="nPr" val=" P " shiftLabel="nCr" shiftVal=" C " type="scientific" />
                <Btn label="STO" type="scientific" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><button onClick={memoryStore} className="w-full bg-zinc-100 dark:bg-black text-zinc-600 dark:text-zinc-300 text-xs font-bold rounded-lg border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800">STO</button></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Store the current result in memory for later use.</p></TooltipContent>
                </Tooltip>

                {/* Row 2 */}
                <Btn label="x²" val="^2" shiftLabel="√" shiftVal="√(" type="scientific" />
                <Btn label="x³" val="^3" shiftLabel="∛" shiftVal="cbrt(" type="scientific" />
                <Btn label="x^y" val="^" shiftLabel="x√" shiftVal="^(1/" type="scientific" /> {/* root approx */}
                <Btn label="log" val="log(" shiftLabel="10^x" shiftVal="10^" type="scientific" />
                <Btn label="ln" val="ln(" shiftLabel="e^x" shiftVal="e^" type="scientific" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><button onClick={memoryRecall} className="w-full bg-zinc-100 dark:bg-black text-zinc-600 dark:text-zinc-300 text-xs font-bold rounded-lg border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800">RCL</button></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Recall the stored memory value and insert it into the current expression.</p></TooltipContent>
                </Tooltip>

                {/* Row 3 */}
                <Btn label="(-)" val="-" type="scientific" /> {/* Negative sign approximation */}
                <Btn label="x⁻¹" val="^(-1)" shiftLabel="!" shiftVal="fact(" type="scientific" />
                <Btn label="sin" val="sin(" shiftLabel="sin⁻¹" shiftVal="asin(" type="scientific" />
                <Btn label="cos" val="cos(" shiftLabel="cos⁻¹" shiftVal="acos(" type="scientific" />
                <Btn label="tan" val="tan(" shiftLabel="tan⁻¹" shiftVal="atan(" type="scientific" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><button onClick={memoryClear} className="w-full bg-zinc-100 dark:bg-black text-red-600 dark:text-red-400 text-xs font-bold rounded-lg border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800">M-CLR</button></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Clear the stored memory value (sets it to zero).</p></TooltipContent>
                </Tooltip>

                {/* Row 4 */}
                <Btn label="(" val="(" type="scientific" />
                <Btn label=")" val=")" type="scientific" />
                <Btn label="hyp" type="scientific" shiftLabel="clr" /> {/* Placeholder for submenu or just toggle? For MVP maybe dedicated keys? Space is tight. Let's make HYP just insert 'h' suffix? No that's complex. Let's use separate row? */}
                <Btn label="sinh" val="sinh(" shiftLabel="asinh" shiftVal="asinh(" type="scientific" />
                <Btn label="cosh" val="cosh(" shiftLabel="acosh" shiftVal="acosh(" type="scientific" />
                <Btn label="tanh" val="tanh(" shiftLabel="atanh" shiftVal="atanh(" type="scientific" />
            </div>

            {/* Numpad Area - 4 columns, outside scientific grid */}
            <div className="grid grid-cols-4 gap-1 auto-rows-min">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><button onClick={clear} className="w-full h-7 md:h-8 rounded-lg bg-rose-600 text-white font-bold hover:bg-rose-700 shadow-sm">AC</button></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">All Clear — reset the calculator and clear the current expression.</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div><button onClick={backspace} className="w-full h-7 md:h-8 rounded-lg bg-zinc-600 text-white font-bold hover:bg-zinc-700 shadow-sm"><Delete className="w-3.5 h-3.5 mx-auto" /></button></div>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-[10px]">Delete the last character from the current expression.</p></TooltipContent>
                </Tooltip>
                <Btn label="%" val="%" type="scientific" />
                <Btn label={<Divide className="w-4 h-4" />} val="÷" type="operator" />

                <Btn label="7" val="7" />
                <Btn label="8" val="8" />
                <Btn label="9" val="9" />
                <Btn label={<MultiplyIcon className="w-4 h-4" />} val="×" type="operator" />

                <Btn label="4" val="4" />
                <Btn label="5" val="5" />
                <Btn label="6" val="6" />
                <Btn label={<Minus className="w-4 h-4" />} val="-" type="operator" />

                <Btn label="1" val="1" />
                <Btn label="2" val="2" />
                <Btn label="3" val="3" />
                <Btn label={<Plus className="w-4 h-4" />} val="+" type="operator" />

                <Btn label="0" val="0" />
                <Btn label="." val="." />
                <Btn label="×10ⁿ" val="*10^" type="scientific" />
                <Btn label="Ans" val="Ans" type="action" />
            </div>

            {/* Equals button - full width */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                    onClick={calculate}
                    className="h-7 md:h-8 rounded-xl bg-emerald-600 text-white font-bold text-xl hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
                >
                    =
                </button>
              </TooltipTrigger>
              <TooltipContent><p className="text-[10px]">Calculate the result of the current expression.</p></TooltipContent>
            </Tooltip>
         </div>

         {/* History Tape */}
         <div className="bg-white dark:bg-zinc-900/50 rounded-2xl border border-zinc-200 dark:border-zinc-800 flex flex-col overflow-hidden h-[600px] lg:h-auto shadow-sm">
            <div className="p-3 border-b border-zinc-100 dark:border-white/5 bg-zinc-50/50 dark:bg-black/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-semibold text-sm">Paper Tape</h3>
                </div>
                <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div><Button variant="ghost" size="sm" onClick={() => setHistory([])} disabled={history.length === 0} className="h-7 px-2 text-xs text-zinc-500 hover:text-red-500">
                            <Trash2 className="w-3 h-3 mr-1" /> Clear
                        </Button></div>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-[10px]">Clear all calculation history from the paper tape.</p></TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link to="/dashboard/utilities/ai-calculator" state={{ from: 'calculator' }} className="inline-flex items-center h-7 px-3 text-xs font-medium rounded-md bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
                            <Sparkles className="w-3 h-3 mr-1" /> Use AI
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-[10px]">Switch to AI Calculator for natural language math questions and visual diagrams.</p></TooltipContent>
                    </Tooltip>
                </div>
            </div>
            <ScrollArea className="flex-1 p-4">
                {history.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-50 space-y-2">
                        <Sigma className="w-8 h-8" />
                        <p className="text-xs">No calculations yet</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {history.map((item) => (
                            <Tooltip key={item.id}>
                              <TooltipTrigger asChild>
                                <button 
                                    onClick={() => setInput(item.result.replace(/,/g, ''))}
                                    className="w-full text-right p-3 rounded-lg hover:bg-white dark:hover:bg-white/5 transition-all group border border-transparent hover:border-gray-100 dark:hover:border-white/5"
                                >
                                    <div className="text-xs text-zinc-400 mb-1">{item.expression} =</div>
                                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400 font-mono group-hover:scale-105 transition-transform origin-right">
                                        {item.result}
                                    </div>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent><p className="text-[10px]">Click to reuse this result in a new calculation.</p></TooltipContent>
                            </Tooltip>
                        ))}
                        <div ref={historyEndRef} />
                    </div>
                )}
            </ScrollArea>
         </div>
      </div>
    </div>
  );
};

export default IndependentCalculatorView;
